import type { BlockOf, BlockType } from "./blocks";
import type { Palette, Typeface } from "./brand";
import type { SiteIntro } from "./intro";
import type {
  NavigationItem,
  PublicCampus,
  PublicEvent,
  PublicForm,
  PublicGroup,
} from "./public-site";
import type { CampusView } from "./campus-pages";
import type { PodcastFeed, PublicSermon, PublicSermonSeries, SermonView } from "./sermons";
import type { DevotionalView, PublicDevotional } from "./devotionals";
import type { PublicProduct } from "./shop";
import type { GuestCheckout } from "./checkout";

/*
 * The theme contract (ADR 0050). A theme is a package, `packages/theme-<key>`, that draws the block catalog: it is
 * handed a block that has already passed its contract and the page context below, and it returns markup. It never
 * fetches anything, never reaches the database and never reads the request: everything a block shows is loaded once
 * by the shared page code in `apps/public-web` and handed to whichever theme draws it. That is ADR 0007's line
 * between content and presentation, made mechanical.
 *
 * This file names the shapes and nothing else, so it carries no rendering technology. The renderer type is a
 * parameter: `apps/public-web` instantiates it with Astro's component type.
 */

/** Who may use a theme: every church, a church on a plan with premium themes (ADR 0055), or one church DCX built it for. */
export type ThemeAvailability = "included" | "premium" | "bespoke";

/** The key every church falls back to, and the theme whose renderers fill any block another theme leaves out. */
export const STANDARD_THEME_KEY = "standard";

/** How the last form submission went, read from the address by the page code (a browser without JavaScript). */
export interface FormState {
  /** The form the last submission belongs to. */
  readonly formId: string | null;
  readonly sent: boolean;
  readonly error: string | null;
}

/** How the last registration for an event date went, read from the address (a browser without JavaScript). */
export interface RegistrationState {
  readonly occurrenceId: string | null;
  readonly status: "registered" | "waitlisted" | null;
  readonly error: string | null;
}

/**
 * The words that go beside a text-message consent box. They are the platform's, reviewed by carriers (10DLC), not the
 * theme's: every theme prints the same disclosure and the same three links, and only decides where they sit.
 */
export interface SmsConsentWords {
  /** The full disclosure, already naming the church. */
  readonly disclosure: string;
  readonly smsTermsUrl: string;
  readonly privacyUrl: string;
  readonly termsUrl: string;
}

/**
 * Everything a block may need beyond its own content, for one page view. Built once per request by the page code;
 * identical for every theme.
 */
export interface PageContext {
  /** The church's name as the site shows it. */
  readonly siteName: string;
  /** The church's own zone, for a date on an event with no campus of its own and a Live now block's times. */
  readonly timeZone: string | undefined;
  /**
   * This request's instant, epoch milliseconds, on the public site's clock: the one instant every block decides by,
   * so a Live now block is live in the same response that page delivery filtered scheduled blocks for (ADR 0056). A
   * theme reads the time here, never from its own clock.
   */
  readonly now: number;
  /**
   * Active campuses (ADR 0036): where service times and addresses come from once a campus carries them, and a Live
   * now block's zone when it names a campus.
   */
  readonly campuses: readonly PublicCampus[];
  /**
   * The campus whose page is being drawn (ADR 0036's `/campuses/<slug>`), when the page is one: a Live now block that
   * names no campus keeps its times in this campus's zone. Absent on the church's own pages.
   */
  readonly campusId?: string | undefined;
  /** Public, active groups (ADR 0040). */
  readonly groups: readonly PublicGroup[];
  /** Public, active events with their upcoming dates (ADR 0041). */
  readonly events: readonly PublicEvent[];
  /** Published sermons the page's sermon feature blocks draw, newest first, and the church's series (ADR 0061). */
  readonly sermons: readonly PublicSermon[];
  readonly sermonSeries: readonly PublicSermonSeries[];
  /**
   * The latest devotionals visitors may see, newest first (ADR 0077), for a devotional block: only on a page that has
   * one, and only published devotionals whose day has begun in the church's zone. Absent or empty elsewhere.
   */
  readonly devotionals?: readonly PublicDevotional[] | undefined;
  /** Products on sale, for a store collection block or the Shop page (ADR 0071); empty on every other page. */
  readonly products: readonly PublicProduct[];
  /**
   * The member portal's Shop, where each product's Buy link goes (ADR 0071): the church's own member address when it
   * prefers it, otherwise HoW's. Null when it cannot be built; a theme then draws no Buy link.
   */
  readonly shopUrl: string | null;
  /** The page's forms, by id, each with the token minted for this view. */
  readonly forms: Readonly<Record<string, PublicForm>>;
  /** The path a form returns the visitor to. */
  readonly returnPath: string;
  /** Where a form posts, and how the last submission went. */
  readonly formAction: string;
  readonly formState: FormState;
  /** Where an event registration posts, and how the last one went. */
  readonly registerAction: string;
  readonly registrationState: RegistrationState;
  /** The one-time code from a text-to-join link (`?j=`, #338), already checked; null otherwise. */
  readonly joinCode: string | null;
  readonly smsConsent: SmsConsentWords;
  /**
   * Guest payment on this website (ADR 0076): where a paid registration or a Shop order posts, whether the church can
   * take a card online now, the page's timing token, how a Shop order reaches a buyer, and how the last payment went.
   * Absent or null where the site offers none (a staff preview, or before the reader exists).
   */
  readonly checkout?: GuestCheckout | null | undefined;
  /** On one product's page in the Shop (`/shop/<id>`, ADR 0076): that product's id. */
  readonly shopProductId?: string | undefined;
}

/** What every renderer is given: one block of its type, and the page context. */
export interface BlockRenderProps<T extends BlockType = BlockType> {
  readonly block: BlockOf<T>;
  readonly ctx: PageContext;
}

/**
 * What a sermon page renderer is given (ADR 0061): the view the database returned for `/sermons`, a series or one
 * sermon, the page context, and whether the church's podcast is on (the archive then links its feed).
 */
export interface SermonPageRenderProps {
  readonly view: SermonView;
  readonly ctx: PageContext;
  readonly podcast: boolean;
}

/**
 * What a campus page renderer is given (ADR 0036 amendment, Milestone 5 B1): the location chooser or one campus with
 * its events, and the page context.
 */
export interface CampusPageRenderProps {
  readonly view: CampusView;
  readonly ctx: PageContext;
}

/** What a devotional page renderer is given (ADR 0077): today's and the archive, a series, or one devotional. */
export interface DevotionalPageRenderProps {
  readonly view: DevotionalView;
  readonly ctx: PageContext;
}

/** What the Shop page renderer is given (ADR 0071): the page context, whose `products` and `shopUrl` it draws. */
export interface ShopPageRenderProps {
  readonly ctx: PageContext;
}

/** The pages a theme may draw besides blocks. Each is optional; the standard theme's is used where one is missing. */
export type ThemePageKind = "sermons" | "campuses" | "shop" | "devotionals";

/**
 * A campus as a theme's header names it (ADR 0073, amended 2026-10-14): enough for a campus chooser and a "This
 * Sunday" card, and nothing else about the place. Built by the page from the campuses it already loaded (ADR 0036).
 */
export interface ChromeCampus {
  readonly slug: string;
  readonly name: string;
  readonly addressLines: readonly string[];
  /** Sunday's service times as the campus keeps them ("10:00", "11:45"), sorted; empty when it lists none. */
  readonly sundayTimes: readonly string[];
  /** The campus's own map link, when the church gave one. */
  readonly directionsUrl: string | null;
  /** The campus a visitor is shown before choosing one: the first the church lists. Exactly one is main. */
  readonly isMain: boolean;
}

/**
 * What a theme's header or footer is given (ADR 0073): the church's name, its logo as the page already resolved it,
 * its two published menus, the address being drawn (so the header can mark where the visitor is), the request's
 * instant (a footer's year is read here, never from the theme's own clock) and the church's listed campuses (a
 * header's campus chooser, amended 2026-10-14). Like a block, the chrome never fetches.
 */
export interface ThemeChromeProps {
  readonly siteName: string;
  /** The logo's public address and the width the church chose, or null when the church set its name in type. */
  readonly logo: { readonly src: string; readonly width: number } | null;
  readonly header: readonly NavigationItem[];
  readonly footer: readonly NavigationItem[];
  /** The path being drawn, such as `/` or `/about`, without a query. */
  readonly path: string;
  readonly now: number;
  /** The church's own zone, for anything dated in the chrome. */
  readonly timeZone: string | undefined;
  /**
   * The church's listed campuses, the main one first, for a header that lets a visitor choose where they are going
   * (Revival's campus chip). Empty for a church with none; a theme that draws no chooser ignores it.
   */
  readonly campuses: readonly ChromeCampus[];
  /**
   * The intro the church asked for (`Brand.intro`, amended 2026-10-14 twice): the spinner (a dark screen for about a
   * second before the page fades in) or its welcome words (the words cycle on the ink, the panel closes into a circle
   * and the hero enters), once per visit; `false` is none. A theme that draws none ignores it.
   */
  readonly intro?: SiteIntro | false | undefined;
}

/**
 * The page chrome a theme may draw itself (ADR 0073). Each is optional; a theme that leaves one out gets the page's
 * own header or footer, styled by its stylesheet, exactly as before.
 */
export type ThemeChromeKind = "header" | "footer";

/** Keep the feed type reachable from the theme contract for renderers that link to it. */
export type { PodcastFeed };

/** A theme's manifest (ADR 0050 section 1). `Renderer` is the rendering technology's component type. */
export interface ThemePackage<Renderer = unknown> {
  /** Stable and lower-case; it is what a church stores (`tenant_settings.branding.theme`). */
  readonly key: string;
  readonly name: string;
  readonly availability: ThemeAvailability;
  /** The brand palettes the theme supports. A church on another palette gets the theme's first. */
  readonly palettes: readonly Palette[];
  /** The typefaces it supports; absent means all of them. */
  readonly typefaces?: readonly Typeface[];
  /**
   * The address of the theme's own stylesheet, loaded after the standard one and only on the pages of churches using
   * this theme. Every rule in it is scoped under `[data-theme="<key>"]`, so it cannot reach another theme's page even
   * if it were loaded there. Absent for the standard theme, whose stylesheet is the base every church page loads: the
   * standard renderers that fill in for a theme's missing blocks need its rules wherever they are drawn.
   */
  readonly stylesheet?: string;
  /**
   * Self-hosted font files (`/fonts/...`, apps/public-web/public/fonts/README.md) every page in this theme draws
   * with, so the page can preload them beside the church's own typeface. Never an address on another host.
   */
  readonly fonts?: readonly string[];
  /**
   * The blocks the theme draws itself. Optional per block: a theme that leaves one out gets the standard theme's
   * renderer, styled by its own stylesheet, so every theme shows every block on the day the block is added.
   */
  readonly renderers: Partial<Record<BlockType, Renderer>>;
  /**
   * Whole pages the theme draws (ADR 0061): `sermons` draws the sermon archive, a series and one sermon from
   * `SermonPageRenderProps`; `campuses` the location chooser and a campus's page from `CampusPageRenderProps`;
   * `devotionals` today's devotional, the archive, a series and one devotional from `DevotionalPageRenderProps`.
   * Optional; a theme that leaves one out gets the standard theme's, under its own stylesheet.
   */
  readonly pages?: Partial<Record<ThemePageKind, Renderer>>;
  /**
   * The header and footer the theme draws (ADR 0073), each given `ThemeChromeProps`. Optional; without one the page
   * draws its own (`.pw-header`, `.pw-footer`), which is what every theme had before.
   */
  readonly chrome?: Partial<Record<ThemeChromeKind, Renderer>>;
}

/** A theme key as a church may store it: lower-case letters, digits and hyphens, starting with a letter. */
export const THEME_KEY_PATTERN = /^[a-z][a-z0-9-]{0,39}$/;
